import styled from "styled-components";
import { useEffect, useState } from "react";
import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import DataTable from "../../Componentes/Utils/DataTable";
import SmallLoading from "../../Componentes/Utils/SmallLoading";
import useApi from "../../hooks/useApi";
import { formatCurrency } from "../../Funcoes/formatCurrency";
import { formatNumber } from "../../Funcoes/formatNumber";
import { useToast } from "../../contexts/ToastContext";

const API_URL = "Painel/API/Controladoria/PedidosCompra/FluxoCaixa/Detalhe/index.php";

const Title = styled.h2`
    margin: 0 0 12px 0;
    font-size: 18px;
    color: var(--ctrl-text, #333);
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px 16px;
    margin-bottom: 18px;

    @media (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const InfoItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const InfoLabel = styled.span`
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    color: var(--ctrl-label, #94a3b8);
`;

const InfoValue = styled.span`
    font-size: 14px;
    color: var(--ctrl-text, #1f2937);
    font-weight: 500;
`;

const StatusValue = styled(InfoValue)`
    color: ${({ status }) =>
        status === 'OK' ? '#2e7d52'
            : status === 'ALERTA' ? '#1d6fb8'
                : status === 'SEM CONDICAO' ? '#b45309'
                    : '#b42318'};
    font-weight: 700;
`;

const EntregaValue = styled(InfoValue)`
    color: ${({ cod }) =>
        cod === 'ENTREGUE_TOTAL' ? '#2e7d52'
            : cod === 'ENTREGUE_PARCIAL' ? '#b45309'
                : '#64748b'};
    font-weight: 700;
`;

const DivergeValue = styled(InfoValue)`
    color: #1d6fb8;
`;

const ParcialBanner = styled.div`
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #fff7ed;
    color: #b45309;
    font-size: 13px;
    border: 1px solid #fed7aa;
`;

const ItemLista = styled.span`
    font-size: 14px;
    color: var(--ctrl-text, #1f2937);
    font-weight: 500;

    & + & {
        margin-top: 2px;
    }
`;

const ListaValores = styled.div`
    display: flex;
    flex-direction: column;
`;

// Exibe uma ou várias linhas (ex.: pedido com rateio em vários centros de custo).
const ValorLista = ({ linhas }) => {
    if (!linhas || linhas.length === 0) {
        return <InfoValue>Não informado</InfoValue>;
    }
    if (linhas.length === 1) {
        return <InfoValue>{linhas[0]}</InfoValue>;
    }
    return (
        <ListaValores>
            {linhas.map((texto, index) => (
                <ItemLista key={index}>{texto}</ItemLista>
            ))}
        </ListaValores>
    );
};

const SectionTitle = styled.h3`
    margin: 18px 0 8px 0;
    font-size: 14px;
    color: var(--ctrl-text-muted, #334155);
    font-weight: 700;
`;

const TableWrapper = styled.div`
    width: 100%;
    max-height: 40dvh;
    overflow: auto;
    border: 1px solid var(--ctrl-border, #ddd);
    border-radius: 8px;
`;

const EmptyText = styled.p`
    margin: 0;
    padding: 12px;
    font-size: 14px;
    color: var(--ctrl-text-muted, #666);
`;

const MotivosBox = styled.div`
    margin-top: 12px;
    padding: 10px 12px;
    border-radius: 6px;
    background: #fef2f2;
    color: #b42318;
    font-size: 13px;
    border: 1px solid #fecaca;
`;

const Coluna = styled.td`
    text-align: ${({ align }) => align || "left"};
    white-space: nowrap;
`;

const TotalRow = styled.tr`
    background-color: var(--ctrl-surface-soft, #f8fafc);
    font-weight: 700;

    & td {
        border-top: 2px solid var(--ctrl-border-soft, #e2e8f0);
    }
`;

const LoadingWrapper = styled.div`
    width: 100%;
    min-height: 120px;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const BankPanel = styled.div`
    margin-top: 8px;
    padding: 12px 14px;
    border-radius: 8px;
    background: #f0f7ff;
    border: 1px solid #cfe3fb;
`;

const BankGrid = styled(InfoGrid)`
    margin-bottom: 0;
`;

const ModalParcelasFluxoCaixa = ({ pedido, onClose }) => {

    const api = useApi();
    const { notifyError } = useToast();

    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [itens, setItens] = useState([]);
    const [totais, setTotais] = useState(null);
    const [fornecedorBanco, setFornecedorBanco] = useState(null);
    const [detalheExtra, setDetalheExtra] = useState({
        dt_finalizacao: null,
        centros_custo: [],
        itens_conta: [],
        solicitantes_lista: [],
    });

    const parcelas = pedido.parcelas || [];
    const motivos = pedido.motivos_inconsistencia || [];
    const divergeCond = pedido.cond_fornecedor && pedido.cond_pagto && pedido.cond_fornecedor !== pedido.cond_pagto;
    const recorrente = !!pedido.recorrente;
    const entregaParcial = pedido.status_entrega === 'ENTREGUE_PARCIAL';

    // Carrega itens, dados bancários, listas de dimensões e finalização sob demanda.
    useEffect(() => {
        let ativo = true;

        const carregarDetalhe = async () => {
            setCarregando(true);
            setErro(null);

            try {
                const params = new URLSearchParams();
                params.append('filial', pedido.filial);
                params.append('num_pedido', pedido.num_pedido);
                if (pedido.cod_fornecedor) params.append('cod_fornecedor', pedido.cod_fornecedor);
                if (pedido.loja_fornecedor) params.append('loja_fornecedor', pedido.loja_fornecedor);

                const { data } = await api.get(`${API_URL}?${params.toString()}`);

                if (!ativo) return;

                setItens(data.itens || []);
                setTotais(data.totais || null);
                setFornecedorBanco(data.fornecedor || null);
                setDetalheExtra({
                    dt_finalizacao: data.dt_finalizacao || null,
                    centros_custo: data.centros_custo || [],
                    itens_conta: data.itens_conta || [],
                    solicitantes_lista: data.solicitantes_lista || [],
                });
            } catch (e) {
                notifyError(e?.response?.data?.message || "Erro ao carregar os itens do pedido.");
                if (ativo) setErro('Erro ao carregar os itens do pedido.');
            } finally {
                if (ativo) setCarregando(false);
            }
        };

        carregarDetalhe();

        return () => {
            ativo = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const ouNaoInformado = (valor) => (valor ? valor : 'Não informado');

    // Junta código e dígito verificador (ex.: agência/conta) quando houver.
    const comDigito = (valor, dv) => {
        if (!valor) return 'Não informado';
        return dv ? `${valor}-${dv}` : valor;
    };

    // Formata "código - descrição" ignorando partes vazias.
    const formatarCodDesc = (cod, desc) => {
        const partes = [cod, desc].map((p) => (p ? String(p).trim() : '')).filter(Boolean);
        return partes.join(' - ');
    };

    // Monta as linhas de exibição a partir da lista completa (quando houver) ou
    // do valor representativo; retorna [] quando não há informação.
    const linhasCodDesc = (lista, codFallback, descFallback) => {
        if (Array.isArray(lista) && lista.length > 0) {
            return lista.map((i) => formatarCodDesc(i.cod, i.desc)).filter(Boolean);
        }
        const unico = formatarCodDesc(codFallback, descFallback);
        return unico ? [unico] : [];
    };

    const linhasTexto = (lista, fallback) => {
        if (Array.isArray(lista) && lista.length > 0) return lista.filter(Boolean);
        return fallback ? [fallback] : [];
    };

    return (
        <Overlay state={true}>
            <Modal size="xl" header close={onClose}>
                <Title>Pedido {pedido.num_pedido}</Title>

                <InfoGrid>
                    <InfoItem>
                        <InfoLabel>Fornecedor</InfoLabel>
                        <InfoValue>{pedido.fornecedor || " - "}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Cód. fornecedor</InfoLabel>
                        <InfoValue>
                            {pedido.cod_fornecedor
                                ? `${pedido.cod_fornecedor}${pedido.loja_fornecedor ? `/${pedido.loja_fornecedor}` : ''}`
                                : " - "}
                        </InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Comprador</InfoLabel>
                        <InfoValue>{pedido.comprador || " - "}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Solicitante</InfoLabel>
                        <ValorLista linhas={linhasTexto(detalheExtra.solicitantes_lista, pedido.solicitante)} />
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Centro de custo</InfoLabel>
                        <ValorLista linhas={linhasCodDesc(detalheExtra.centros_custo, pedido.centro_custo, pedido.centro_custo_desc)} />
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Conta contábil (item conta)</InfoLabel>
                        <ValorLista linhas={linhasCodDesc(detalheExtra.itens_conta, pedido.item_conta, pedido.item_conta_desc)} />
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Cond. pagto</InfoLabel>
                        <InfoValue>{pedido.cond_pagto || " - "}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Forma pagto</InfoLabel>
                        <InfoValue>{pedido.forma_pagto || pedido.forma_pagto_cod || " - "}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Cond. fornecedor</InfoLabel>
                        {divergeCond ? (
                            <DivergeValue title="Condição padrão do fornecedor (A2_COND)">{pedido.cond_fornecedor}</DivergeValue>
                        ) : (
                            <InfoValue>{pedido.cond_fornecedor || " - "}</InfoValue>
                        )}
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Descrição condição</InfoLabel>
                        <InfoValue>{pedido.desc_cond_pagto || " - "}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Emissão</InfoLabel>
                        <InfoValue>{pedido.dt_emissao || " - "}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Prev. entrega</InfoLabel>
                        <InfoValue>{pedido.dt_entrega || " - "}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Status de entrega</InfoLabel>
                        <EntregaValue cod={pedido.status_entrega}>
                            {pedido.status_entrega_label || 'Pendente'}
                        </EntregaValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Data de finalização</InfoLabel>
                        <InfoValue>
                            {carregando
                                ? 'Carregando...'
                                : ouNaoInformado(detalheExtra.dt_finalizacao)}
                        </InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Valor total</InfoLabel>
                        <InfoValue>{`R$ ${formatCurrency(pedido.valor_total)}`}</InfoValue>
                    </InfoItem>
                    <InfoItem>
                        <InfoLabel>Status</InfoLabel>
                        <StatusValue status={pedido.status_consistencia}>{pedido.status_consistencia || " - "}</StatusValue>
                    </InfoItem>
                </InfoGrid>

                <SectionTitle>Dados bancários do fornecedor</SectionTitle>
                <BankPanel>
                    {carregando ? (
                        <LoadingWrapper>
                            <SmallLoading text="Carregando dados..." />
                        </LoadingWrapper>
                    ) : (
                    <BankGrid>
                        <InfoItem>
                            <InfoLabel>Fornecedor</InfoLabel>
                            <InfoValue>{ouNaoInformado(fornecedorBanco?.nome || pedido.fornecedor)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>CNPJ / CPF</InfoLabel>
                            <InfoValue>{ouNaoInformado(fornecedorBanco?.cnpj)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Banco</InfoLabel>
                            <InfoValue>{ouNaoInformado(formatarCodDesc(fornecedorBanco?.banco_cod, fornecedorBanco?.banco_nome) || null)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Tipo de conta</InfoLabel>
                            <InfoValue>{ouNaoInformado(fornecedorBanco?.tipo)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Agência</InfoLabel>
                            <InfoValue>{comDigito(fornecedorBanco?.agencia, fornecedorBanco?.agencia_dv)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Conta</InfoLabel>
                            <InfoValue>{comDigito(fornecedorBanco?.conta, fornecedorBanco?.conta_dv)}</InfoValue>
                        </InfoItem>
                        <InfoItem>
                            <InfoLabel>Chave PIX</InfoLabel>
                            <InfoValue>{ouNaoInformado(fornecedorBanco?.pix)}</InfoValue>
                        </InfoItem>
                    </BankGrid>
                    )}
                </BankPanel>

                {entregaParcial && (
                    <ParcialBanner>
                        Pedido com <b>entrega parcial</b>
                        {pedido.qtd_entregue != null && pedido.qtd_total != null
                            ? ` — ${pedido.qtd_entregue} de ${pedido.qtd_total} entregue(s).`
                            : '.'}
                    </ParcialBanner>
                )}

                <SectionTitle>Itens do pedido</SectionTitle>
                {carregando ? (
                    <LoadingWrapper>
                        <SmallLoading text="Carregando itens do pedido..." />
                    </LoadingWrapper>
                ) : erro ? (
                    <EmptyText>{erro}</EmptyText>
                ) : itens.length === 0 ? (
                    <EmptyText>Nenhum item encontrado neste pedido.</EmptyText>
                ) : (
                    <TableWrapper>
                        <DataTable>
                            <thead>
                                <tr>
                                    <th>ITEM</th>
                                    <th>PRODUTO</th>
                                    <th>DESCRIÇÃO</th>
                                    <th>GRUPO</th>
                                    <th>UM</th>
                                    <th>QTD</th>
                                    <th>ENTREGUE</th>
                                    <th>PENDENTE</th>
                                    <th>PREÇO UNIT.</th>
                                    <th>PRODUTOS</th>
                                    <th>IPI</th>
                                    <th>FRETE</th>
                                    <th>SEGURO</th>
                                    <th>DESPESA</th>
                                    <th>DESCONTO</th>
                                    <th>TOTAL</th>
                                    <th>CENTRO DE CUSTO</th>
                                    <th>CONTA CONTÁBIL</th>
                                    <th>ITEM CONTA</th>
                                    <th>ARMAZÉM</th>
                                </tr>
                            </thead>
                            <tbody>
                                {itens.map((item, index) => (
                                    <tr key={`${item.item}-${index}`}>
                                        <Coluna align="center">{item.item || " - "}</Coluna>
                                        <td>{item.produto || " - "}</td>
                                        <td>{item.descricao || " - "}</td>
                                        <td>{formatarCodDesc(item.grupo, item.desc_grupo) || " - "}</td>
                                        <Coluna align="center">{item.unidade || " - "}</Coluna>
                                        <Coluna align="right">{formatNumber(item.quantidade)}</Coluna>
                                        <Coluna align="right">{formatNumber(item.qtd_entregue)}</Coluna>
                                        <Coluna align="right">{formatNumber(item.qtd_pendente)}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(item.preco_unit)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(item.vlr_produtos)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(item.vlr_ipi)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(item.vlr_frete)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(item.vlr_seguro)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(item.vlr_despesa)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(item.vlr_desconto)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(item.vlr_total)}`}</Coluna>
                                        <td>{formatarCodDesc(item.centro_custo, item.centro_custo_desc) || " - "}</td>
                                        <td>{formatarCodDesc(item.conta_contabil, item.conta_contabil_desc) || " - "}</td>
                                        <td>{formatarCodDesc(item.item_conta, item.item_conta_desc) || " - "}</td>
                                        <Coluna align="center">{item.armazem || " - "}</Coluna>
                                    </tr>
                                ))}
                            </tbody>
                            {totais && (
                                <tfoot>
                                    <TotalRow>
                                        <td colSpan={9}>Totais</td>
                                        <Coluna align="right">{`R$ ${formatCurrency(totais.vlr_produtos)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(totais.vlr_ipi)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(totais.vlr_frete)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(totais.vlr_seguro)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(totais.vlr_despesa)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(totais.vlr_desconto)}`}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(totais.vlr_total)}`}</Coluna>
                                        <td colSpan={4}></td>
                                    </TotalRow>
                                </tfoot>
                            )}
                        </DataTable>
                    </TableWrapper>
                )}

                <SectionTitle>Parcelas previstas</SectionTitle>
                {parcelas.length === 0 ? (
                    <EmptyText>Nenhuma parcela prevista para este pedido.</EmptyText>
                ) : (
                    <TableWrapper>
                        <DataTable>
                            <thead>
                                <tr>
                                    <th>PARCELA</th>
                                    {recorrente ? <th>COMPETÊNCIA</th> : null}
                                    <th>PRAZO</th>
                                    <th>DATA PREVISTA</th>
                                    <th>RATEIO</th>
                                    <th>VALOR</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parcelas.map((parcela, index) => (
                                    <tr key={index}>
                                        <Coluna align="center">{recorrente ? index + 1 : (parcela.parcela ?? parcela.item ?? index + 1)}</Coluna>
                                        {recorrente ? <Coluna align="center">{parcela.competencia_fmt || " - "}</Coluna> : null}
                                        <Coluna align="center">{parcela.dias != null ? `${parcela.dias}d` : " - "}</Coluna>
                                        <td>{parcela.data_prevista_fmt || " - "}</td>
                                        <Coluna align="right">{parcela.rateio != null ? `${parcela.rateio}%` : " - "}</Coluna>
                                        <Coluna align="right">{`R$ ${formatCurrency(parcela.valor)}`}</Coluna>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot>
                                <TotalRow>
                                    <td colSpan={recorrente ? 5 : 4}>Valor total</td>
                                    <Coluna align="right">{`R$ ${formatCurrency(pedido.valor_total)}`}</Coluna>
                                </TotalRow>
                            </tfoot>
                        </DataTable>
                    </TableWrapper>
                )}

                {motivos.length > 0 && (
                    <MotivosBox>{motivos.join(' ')}</MotivosBox>
                )}
            </Modal>
        </Overlay>
    );
};

export default ModalParcelasFluxoCaixa;
